import { useCallback } from 'react';
import { useNotificationStore } from './store';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DataEditor from '@/components/DataEditor';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Box } from '@mui/material';
import dynamic from 'next/dynamic';

// The markdown stack (~100 KiB) is needed only once a notification opens, not on every first load
const Markdown = dynamic(() => import('@/components/Markdown'));

export default function Form() {
  const { notification, handleClose } = useNotificationStore();

  const onClose = useCallback(() => {
    handleClose();
    notification.onCancel?.();
  }, [handleClose, notification]);

  const onSubmit = useCallback(
    (values: Record<string, any>) => {
      handleClose();
      notification.onSubmit?.(values);
    },
    [handleClose, notification],
  );

  if (notification.type !== 'form') return null;

  return (
    <Dialog
      open={notification.open ?? false}
      onClose={onClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      maxWidth='sm'
      fullWidth
      data-lenis-prevent
    >
      <DialogTitle
        id='alert-dialog-title'
        sx={{
          position: 'relative',
          bgcolor: 'background.paper',
          borderRadius: 'var(--mui-shape-borderRadius) var(--mui-shape-borderRadius) 0 0',
          pr: 2,
        }}
      >
        {notification.title}
        <IconButton
          aria-label='close'
          onClick={onClose}
          sx={{ position: 'absolute', right: 'var(--mui-spacing)' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 0, mt: 1 }}>
        {notification.message && (
          <Box id='alert-dialog-description' sx={{ color: 'text.secondary' }}>
            <Markdown markdown={notification.message} />
          </Box>
        )}
      </DialogContent>

      <DialogContent sx={{ p: 0 }}>
        <DataEditor
          formFields={notification.formFields ?? []}
          onCancel={onClose}
          onSubmit={onSubmit}
          defaultValues={notification.defaultValues ?? {}}
          showStickyButtons
          sx={{
            p: 1.25,
            pt: 0,
          }}
          submitText={notification.confirmText}
        />
      </DialogContent>
    </Dialog>
  );
}
